/**
 * Explore tab (redesigned 2026-10-17): "Explore Templates" header, search bar,
 * filter chips and a 2-column grid of every active template.
 *
 * Data: the whole catalog is small (CMS-managed, dozens of templates), so it is
 * loaded once (GET /templates, paged with the max limit) and cached. Search and
 * filters run client-side over that list; there is no server search endpoint.
 *
 * Chips: All · Trending (hot badge or the "trending" category) · Video · Photo ·
 * New · then the CMS categories in order. Only the first row is shown until the
 * tune button expands the rest.
 *
 * No like/view counts are shown: `viewSeed` is seed data, not real activity.
 */
import { api } from '../api/client.js';
import { icon } from '../components/icon.js';
import { CATEGORY_ICONS } from '../components/categoryIcons.js';
import { escapeHtml } from '../components/placeholder.js';
import {
  bindCardImages,
  templateCardHtml,
  templateCardSkeletonHtml,
} from '../components/templateCard.js';
import { toastApiError } from '../ui/toast.js';
import type { Category, Template } from '@loomia/shared';

interface Chip {
  id: string;
  label: string;
  icon: string;
  match: (t: Template) => boolean;
}

const COLLAPSED_CHIPS = 7;
const PAGE = 100;

// Survive leaving and returning to the tab.
let catalog: Template[] | null = null;
let categoriesCache: Category[] | null = null;
const state = { q: '', chip: 'all', expanded: false };

async function loadCatalog(): Promise<Template[]> {
  if (catalog) return catalog;
  const all: Template[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < 20; i++) {
    const page = await api.listTemplates({ limit: PAGE, ...(cursor ? { cursor } : {}) });
    all.push(...page.templates);
    if (!page.nextCursor) break;
    cursor = page.nextCursor;
  }
  catalog = all;
  return all;
}

/** A template is listed under its primary category and each extra one. */
function inCategory(t: Template, categoryId: string): boolean {
  return t.categoryId === categoryId || (t.extraCategoryIds?.includes(categoryId) ?? false);
}

function buildChips(categories: Category[]): Chip[] {
  const trendingId = categories.find((c) => c.slug === 'trending')?.id;
  return [
    { id: 'all', label: 'All', icon: 'grid_view', match: () => true },
    {
      id: 'trending',
      label: 'Trending',
      icon: 'local_fire_department',
      match: (t) => t.badges.includes('hot') || (!!trendingId && inCategory(t, trendingId)),
    },
    { id: 'video', label: 'Video', icon: 'play_circle', match: (t) => t.mediaKind === 'video' },
    { id: 'photo', label: 'Photo', icon: 'image', match: (t) => t.mediaKind === 'image' },
    { id: 'new', label: 'New', icon: 'auto_awesome', match: (t) => t.badges.includes('new') || t.isNew },
    ...categories
      .filter((c) => c.slug !== 'trending')
      .map((c) => ({
        id: `cat:${c.id}`,
        label: c.title,
        icon: CATEGORY_ICONS[c.slug] ?? 'auto_awesome',
        match: (t: Template) => inCategory(t, c.id),
      })),
  ];
}

export function renderExplore(root: HTMLElement): void {
  let alive = true;
  let chips: Chip[] = buildChips(categoriesCache ?? []);

  root.innerHTML = `
    <div class="explore">
      <header class="ex-head">
        <h1 class="ex-title"><span class="ex-grad">Explore</span> Templates</h1>
        <p class="ex-sub">Turn ideas into stunning creations with AI ${icon('auto_awesome', { fill: true })}</p>
      </header>

      <div class="ex-search">
        <span class="ex-search-ic">${icon('search')}</span>
        <input id="ex-q" type="search" inputmode="search" enterkeyhint="search" autocomplete="off"
          placeholder="Search templates, styles, effects…" aria-label="Search templates" value="${escapeHtml(state.q)}" />
        <button class="ex-clear" id="ex-clear" type="button" aria-label="Clear search" hidden>${icon('close')}</button>
        <button class="ex-tune" id="ex-tune" type="button" aria-label="More filters" aria-expanded="${state.expanded}">${icon('tune')}</button>
      </div>

      <div class="ex-chips" id="ex-chips" role="tablist" aria-label="Filters"></div>
      <p class="ex-count" id="ex-count" aria-live="polite"></p>
      <div class="tpl-grid" id="ex-grid">${templateCardSkeletonHtml(6)}</div>
    </div>`;

  const q = root.querySelector<HTMLInputElement>('#ex-q')!;
  const clear = root.querySelector<HTMLButtonElement>('#ex-clear')!;
  const tune = root.querySelector<HTMLButtonElement>('#ex-tune')!;
  const chipsEl = root.querySelector<HTMLElement>('#ex-chips')!;
  const grid = root.querySelector<HTMLElement>('#ex-grid')!;
  const count = root.querySelector<HTMLElement>('#ex-count')!;

  const paintChips = (): void => {
    const visible = state.expanded ? chips : chips.slice(0, COLLAPSED_CHIPS);
    // Never hide the active chip behind the fold.
    const active = chips.find((c) => c.id === state.chip);
    const list = active && !visible.includes(active) ? [...visible.slice(0, -1), active] : visible;
    chipsEl.innerHTML = list
      .map(
        (c) => `<button class="ex-chip${c.id === state.chip ? ' is-active' : ''}" type="button" role="tab"
          aria-selected="${c.id === state.chip}" data-chip="${escapeHtml(c.id)}">${icon(c.icon, { fill: c.id === state.chip })}${escapeHtml(c.label)}</button>`,
      )
      .join('');
    tune.setAttribute('aria-expanded', String(state.expanded));
    tune.classList.toggle('is-on', state.expanded);
    tune.hidden = chips.length <= COLLAPSED_CHIPS;
  };

  const paintGrid = (): void => {
    if (!catalog) return;
    const term = state.q.trim().toLowerCase();
    const chip = chips.find((c) => c.id === state.chip) ?? chips[0]!;
    const hits = catalog.filter(
      (t) =>
        chip.match(t) &&
        (term === '' || `${t.title} ${t.description}`.toLowerCase().includes(term)),
    );
    clear.hidden = state.q === '';
    count.textContent = `${hits.length} ${hits.length === 1 ? 'template' : 'templates'}`;
    if (hits.length === 0) {
      grid.className = '';
      grid.innerHTML = `
        <div class="empty">
          <div class="empty-icon">${icon('search')}</div>
          <p class="muted">No templates match${term ? ` “${escapeHtml(state.q.trim())}”` : ''}.</p>
          <button class="btn btn-secondary" id="ex-reset" type="button">Reset filters</button>
        </div>`;
      return;
    }
    grid.className = 'tpl-grid';
    grid.innerHTML = hits.map((t) => templateCardHtml(t, { explore: true })).join('');
    bindCardImages(grid);
  };

  // --- events ---------------------------------------------------------------------------
  let debounce: number | undefined;
  q.addEventListener('input', () => {
    state.q = q.value;
    window.clearTimeout(debounce);
    debounce = window.setTimeout(paintGrid, 120);
  });
  q.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') q.blur(); // dismiss the mobile keyboard
  });
  root.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const chipBtn = t.closest<HTMLElement>('[data-chip]');
    if (chipBtn?.dataset.chip) {
      state.chip = chipBtn.dataset.chip;
      paintChips();
      paintGrid();
    } else if (t.closest('#ex-clear')) {
      state.q = '';
      q.value = '';
      paintGrid();
      q.focus();
    } else if (t.closest('#ex-tune')) {
      state.expanded = !state.expanded;
      paintChips();
    } else if (t.closest('#ex-reset')) {
      state.q = '';
      state.chip = 'all';
      q.value = '';
      paintChips();
      paintGrid();
    } else if (t.closest('#ex-retry')) {
      void load();
    }
  });

  // --- data ---------------------------------------------------------------------------------
  const load = async (): Promise<void> => {
    try {
      if (!categoriesCache) {
        try {
          categoriesCache = [...(await api.listCategories())].sort((a, b) => a.sortOrder - b.sortOrder);
        } catch {
          categoriesCache = []; // chips still work without categories
        }
      }
      chips = buildChips(categoriesCache);
      await loadCatalog();
      if (!alive) return;
      if (!chips.some((c) => c.id === state.chip)) state.chip = 'all';
      paintChips();
      paintGrid();
    } catch (err) {
      if (!alive) return;
      toastApiError(err);
      grid.className = '';
      grid.innerHTML = `
        <div class="empty">
          <div class="empty-icon">${icon('cloud_off')}</div>
          <p class="muted">Couldn’t load templates.</p>
          <button class="btn btn-secondary" id="ex-retry" type="button">Retry</button>
        </div>`;
    }
  };

  paintChips();
  clear.hidden = state.q === '';
  // Stop touching the DOM once the tab is unmounted.
  const mo = new MutationObserver(() => {
    if (!root.isConnected) {
      alive = false;
      mo.disconnect();
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
  void load();
}
